import Button, { ButtonTypes } from "atoms/buttons/button";
import ChatCitation, {
    CitationInfo,
} from "organisms/modals/chat-modal/chat-citation";
import ConversationMessageRecord from "models/view-models/conversations/conversation-message-record";
import React, {
    useCallback,
    useState,
    useRef,
    useMemo,
    useEffect,
} from "react";
import ReactMarkdown from "react-markdown";
import { ErrorBoundary } from "react-error-boundary";
import { ButtonStyles } from "atoms/constants/button-styles";
import { ConversationMessageStatus } from "models/view-models/conversations/conversation-message-status";
import { ConversationStateStatus } from "utilities/atoms/conversations/current-conversation/conversation-atom.reducer";
import { FeedbackRating } from "models/enumerations/messages/feedback-rating";
import { MessageRole } from "models/enumerations/conversations/message-role";
import { useConversation } from "utilities/atoms/conversations/current-conversation/use-conversation.hook";
import { useLocalization } from "utilities/hooks/use-localization";
import { ChatMessageEditor } from "organisms/modals/chat-modal/chat-message-editor";
import Paragraph from "atoms/typography/paragraph";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import { customComponents } from "./markdown-utils/custom-components";
import { replaceCitations } from "./markdown-utils/replace-citations";
import InlineCitationButton, {
    CitationProps,
} from "./markdown-utils/inline-citation-button";
import rehypeKatex from "rehype-katex";
// @ts-ignore
import "katex/dist/katex.min.css";
import { CitationNode } from "./markdown-utils/types";
import { useCitations } from "utilities/hooks/domain/chat/use-citations";
import { CollectionUtils } from "utilities/collection-utils";
import { FeedbackCategory } from "models/enumerations/messages/feedback-category";
import ConversationMessageFeedbackRecord from "models/view-models/conversations/conversation-message-feedback-record";
import { ChatFinishReason } from "models/enumerations/conversations/chat-finish-reason";
import { MathWithPopup } from "organisms/modals/chat-modal/chat-math-popup";
import CasiThinkingVideo from "./casi-thinking-video";
import { useEvents } from "utilities/contexts/events/events-provider";
import { MessageEventActions } from "models/constants/events/chat-event-actions";
import ConversationApplicationContextRecord from "models/view-models/conversations/conversation-application-context-record";
import ChatMessageAssistantActions from "./chat-message-assistant-actions";
import ChatMessageUserActions from "./chat-message-user-actions";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

export interface ChatMessageProps {
    isEditing?: boolean;
    message: ConversationMessageRecord;
    onCitationClick?: () => void;
    onRetryClick: (message: ConversationMessageRecord) => void;
    onEditMessage?: (id?: string) => void;
    conversationId?: string;
    applicationContext?: ConversationApplicationContextRecord;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const BASE_CLASS = "c-chat-modal-message";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ChatMessage: React.FC<ChatMessageProps> = ({
    message,
    isEditing = false,
    onCitationClick,
    onRetryClick,
    onEditMessage,
    conversationId,
    applicationContext,
}) => {
    const { t } = useLocalization();
    const { createMessageEvent } = useEvents();
    const {
        status,
        createMessageRating,
        updateMessageRating,
        currentConversation,
        edit,
    } = useConversation();

    const [thumbsUpClicked, setThumbsUpClicked] = useState(false);
    const [thumbsDownClicked, setThumbsDownClicked] = useState(false);
    const [editingUserMessage, setEditingUserMessage] =
        useState<ConversationMessageRecord>();
    const [citationPreview, setCitationPreview] = useState<CitationInfo>();
    const [feedbackCategory, setFeedbackCategory] =
        useState<FeedbackCategory>();
    const [showAdditionalFeedback, setShowAdditionalFeedback] = useState(false);

    // Centralized feedback state reset function
    const resetFeedbackState = useCallback(() => {
        setThumbsUpClicked(false);
        setThumbsDownClicked(false);
        setShowAdditionalFeedback(false);
        setFeedbackCategory(undefined);
    }, []);

    // Reset feedback state when conversation status changes (when streaming starts)
    useEffect(() => {
        if (status === ConversationStateStatus.StreamingMessage) {
            resetFeedbackState();
        }
    }, [status, resetFeedbackState]);

    const citations = useMemo(
        () => message.getCitations(currentConversation?.messages ?? []),
        [message, currentConversation?.messages]
    );

    const { citationMap, nextNumberRef, citationsList } = useCitations(
        message,
        citations
    );

    const eventSessionId = conversationId ?? currentConversation?.id;

    const previewRef = useRef<HTMLDivElement>(null);
    const additionalFeedbackRef = useRef<HTMLDivElement>(null);

    const handleCitationClick = useCallback(
        (node: CitationNode) => {
            const citation = citations.at(node.properties.displayNumber - 1);
            if (citation) {
                if (message.id && eventSessionId) {
                    createMessageEvent(
                        message.id,
                        eventSessionId,
                        MessageEventActions.InlineCitationClicked
                    );
                }
                setCitationPreview({
                    citation,
                    displayNumber: node.properties.displayNumber,
                });
            }
        },
        [citations, createMessageEvent, eventSessionId, message.id]
    );

    useEffect(() => {
        if (!isEditing) {
            setEditingUserMessage(undefined);
            return;
        }

        if (message.role === MessageRole.User) {
            setEditingUserMessage(message);
        }
    }, [isEditing, message]);

    useEffect(() => {
        if (citationPreview && previewRef.current) {
            previewRef.current.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    }, [citationPreview]);

    useEffect(() => {
        if (showAdditionalFeedback && additionalFeedbackRef.current) {
            // Use requestAnimationFrame to ensure the component is fully rendered before scrolling
            requestAnimationFrame(() => {
                additionalFeedbackRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                });
            });
        }
    }, [showAdditionalFeedback]);

    // Reset feedback state when the conversation changes or new messages are added
    useEffect(() => {
        resetFeedbackState();
    }, [
        currentConversation?.id,
        currentConversation?.messages?.length,
        resetFeedbackState,
    ]);

    // Reset feedback state when the message changes (when a new message is added)
    useEffect(() => {
        resetFeedbackState();
    }, [message.id, resetFeedbackState]);

    let messageRole: string;
    switch (message.role) {
        case MessageRole.Assistant:
            messageRole = "assistant";
            break;
        case MessageRole.User:
            messageRole = "user";
            break;
        default:
            messageRole = "unknown";
            break;
    }

    const isAssistantMessage = message.role === MessageRole.Assistant;

    const handleCopyClick = () => {
        navigator.clipboard.writeText(message.content ?? "");
        if (message.id && eventSessionId) {
            createMessageEvent(
                message.id,
                eventSessionId,
                MessageEventActions.MessageCopied
            );
        }
    };

    const handleThumbsUpClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.preventDefault();
            if (currentConversation?.id == null) {
                return;
            }

            // Prevent rapid clicking
            if (thumbsUpClicked) {
                setShowAdditionalFeedback(true);
                return;
            }

            setThumbsUpClicked(true);
            setThumbsDownClicked(false);
            setShowAdditionalFeedback(true);

            // If feedback already exists, update it; otherwise create new
            if (message.feedback?.feedbackId) {
                const updatedFeedback = new ConversationMessageFeedbackRecord({
                    feedbackId: message.feedback.feedbackId,
                    rating: FeedbackRating.ThumbsUp,
                    conversationId: currentConversation.id,
                    messageId: message.id,
                });
                updateMessageRating(updatedFeedback);
            } else {
                const feedback = new ConversationMessageFeedbackRecord({
                    rating: FeedbackRating.ThumbsUp,
                    conversationId: currentConversation.id,
                    messageId: message.id,
                });
                createMessageRating(feedback);
            }
        },
        [
            currentConversation?.id,
            thumbsUpClicked,
            message.feedback,
            message.id,
            updateMessageRating,
            createMessageRating,
        ]
    );

    const handleThumbsDownClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.preventDefault();

            if (currentConversation?.id == null) {
                return;
            }

            // Prevent rapid clicking
            if (thumbsDownClicked) {
                setShowAdditionalFeedback(true);
                return;
            }

            setThumbsDownClicked(true);
            setThumbsUpClicked(false);
            setShowAdditionalFeedback(true);

            // If feedback already exists, update it; otherwise create new
            if (message.feedback?.feedbackId) {
                const updatedFeedback = new ConversationMessageFeedbackRecord({
                    feedbackId: message.feedback.feedbackId,
                    rating: FeedbackRating.ThumbsDown,
                    conversationId: currentConversation.id,
                    messageId: message.id,
                });
                updateMessageRating(updatedFeedback);
            } else {
                const feedback = new ConversationMessageFeedbackRecord({
                    rating: FeedbackRating.ThumbsDown,
                    conversationId: currentConversation.id,
                    messageId: message.id,
                });
                createMessageRating(feedback);
            }
        },
        [
            currentConversation?.id,
            message.feedback?.feedbackId,
            message.id,
            thumbsDownClicked,
            updateMessageRating,
            createMessageRating,
        ]
    );

    const handleCloseAdditionalFeedback = () => {
        setFeedbackCategory(undefined);
        setShowAdditionalFeedback(false);
    };

    const handleEditClick = () => {
        if (onEditMessage == null) {
            return;
        }

        onEditMessage(message.id);
    };

    const handleParentEditClick = () => {
        if (onEditMessage == null || message.parentId == null) {
            return;
        }

        onEditMessage(message.parentId);
    };

    const handleRetry = (
        action:
            | typeof MessageEventActions.MessageRetried
            | typeof MessageEventActions.MessageRegenerated
    ) => {
        if (message.id && eventSessionId) {
            createMessageEvent(message.id, eventSessionId, action);
        }
        onRetryClick(message);
    };

    if (message.status === ConversationMessageStatus.Thinking) {
        return (
            <div className={`${BASE_CLASS}__message-wrapper`}>
                <CasiThinkingVideo color={"grey"} />
                <div className={`${BASE_CLASS}__${messageRole}-thinking`}>
                    <div className={`${BASE_CLASS}__thinking`}>
                        {t("thinking")}
                        <span className="thinking-dot"></span>
                        <span className="thinking-dot"></span>
                        <span className="thinking-dot"></span>
                    </div>
                </div>
            </div>
        );
    }

    if (
        message.role === MessageRole.System ||
        message.role === MessageRole.Tool
    ) {
        return <></>;
    }

    if (editingUserMessage) {
        return (
            <ChatMessageEditor
                message={editingUserMessage}
                onClose={(saveChanges) => {
                    if (saveChanges && editingUserMessage.content?.trim()) {
                        edit(
                            editingUserMessage.content,
                            editingUserMessage.id,
                            applicationContext
                        );
                        if (editingUserMessage.id && eventSessionId) {
                            createMessageEvent(
                                editingUserMessage.id,
                                eventSessionId,
                                MessageEventActions.MessageEdited
                            );
                        }
                    }
                    setEditingUserMessage(undefined);
                    onEditMessage?.(undefined);
                }}
                onMessageChange={setEditingUserMessage}
            />
        );
    }

    if (message.status === ConversationMessageStatus.Cancelled) {
        return (
            <CancelledOrErroredMessage
                messageRole={messageRole}
                onRetryClick={() =>
                    handleRetry(MessageEventActions.MessageRetried)
                }
            />
        );
    }

    return (
        <>
            <div className={`${BASE_CLASS}__${messageRole}`}>
                {isAssistantMessage ? (
                    <>
                        <MemoizedMarkdown
                            content={message.content ?? ""}
                            citationMap={citationMap}
                            nextNumberRef={nextNumberRef}
                            handleCitationClick={handleCitationClick}
                        />

                        <FinishReasonMessage
                            message={message}
                            onEditClick={handleParentEditClick}
                            onRetryClick={() =>
                                handleRetry(
                                    MessageEventActions.MessageRegenerated
                                )
                            }
                        />

                        <div>
                            {CollectionUtils.hasValues(citationsList) && (
                                <ChatCitation
                                    citations={citationsList}
                                    onCitationClick={onCitationClick}
                                    conversationId={eventSessionId}
                                    citationPreview={citationPreview}
                                    messageId={message.id}
                                    setCitationPreview={setCitationPreview}
                                    previewRef={previewRef}
                                />
                            )}
                        </div>
                    </>
                ) : (
                    <Paragraph>{message.content ?? ""}</Paragraph>
                )}
            </div>
            {isAssistantMessage &&
                status === ConversationStateStatus.ConversationReady && (
                    <ChatMessageAssistantActions
                        additionalFeedbackRef={additionalFeedbackRef}
                        conversationStatus={status}
                        feedbackCategory={feedbackCategory}
                        handleCloseAdditionalFeedback={
                            handleCloseAdditionalFeedback
                        }
                        handleCopyClick={handleCopyClick}
                        handleThumbsUpClick={handleThumbsUpClick}
                        handleThumbsDownClick={handleThumbsDownClick}
                        handleRetry={handleRetry}
                        message={message}
                        setFeedbackCategory={setFeedbackCategory}
                        setShowAdditionalFeedback={setShowAdditionalFeedback}
                        showAdditionalFeedback={showAdditionalFeedback}
                        thumbsDownClicked={thumbsDownClicked}
                        thumbsUpClicked={thumbsUpClicked}
                    />
                )}

            {message.role === MessageRole.User && (
                <ChatMessageUserActions
                    conversationStatus={status}
                    handleCopyClick={handleCopyClick}
                    handleEditClick={handleEditClick}
                />
            )}
        </>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

const compareMessageProps = (
    prev: Readonly<ChatMessageProps>,
    next: Readonly<ChatMessageProps>
) =>
    prev.conversationId === next.conversationId &&
    prev.isEditing === next.isEditing &&
    prev.message.id === next.message.id &&
    prev.message.content === next.message.content &&
    prev.message.status === next.message.status &&
    prev.message.role === next.message.role &&
    prev.message.citations === next.message.citations &&
    prev.applicationContext === next.applicationContext;

export default React.memo(ChatMessage, compareMessageProps);

// #endregion Exports

type MemoizedMarkdownProps = {
    content: string;
    citationMap: Map<number, number>;
    nextNumberRef: React.MutableRefObject<number>;
    handleCitationClick: (node: CitationNode) => void;
};

const compareMarkdownProps = (
    prev: Readonly<MemoizedMarkdownProps>,
    next: Readonly<MemoizedMarkdownProps>
) => prev.content === next.content;

// 12/09/2025 14:45:00
// Good luck and god speed.
function wrapLatexLines(markdown: string): string {
    return markdown
        .replaceAll("\\%Z", "%Z")
        .replaceAll("%Z", "\\%Z")
        .replaceAll("\\[", "$ ")
        .replaceAll("\\]", " $")
        .replaceAll("\\(", "$\\left(")
        .replaceAll("\\)", "\\right)$");
}

interface CancelledOrErroredMessageProps {
    messageRole: string;
    onRetryClick: () => void;
}

function CancelledOrErroredMessage({
    messageRole,
    onRetryClick,
}: CancelledOrErroredMessageProps) {
    return (
        <div className={`${BASE_CLASS}__${messageRole}`}>
            <Paragraph cssClassName={`${BASE_CLASS}__markdown-paragraph`}>
                This message was cancelled or there was an error in the
                response. Would you like to{" "}
                <Button
                    type={ButtonTypes.Button}
                    style={ButtonStyles.Anchor}
                    onClick={onRetryClick}>
                    retry
                </Button>
                ?
            </Paragraph>
        </div>
    );
}

interface FinishReasonMessageProps {
    message: ConversationMessageRecord;
    onEditClick?: () => void;
    onRetryClick?: () => void;
}

function FinishReasonMessage({
    message,
    onEditClick,
    onRetryClick,
}: FinishReasonMessageProps) {
    const normalMessage =
        message.chatFinishReason == null ||
        message.chatFinishReason === ChatFinishReason.ToolCalls ||
        message.chatFinishReason === ChatFinishReason.FunctionCall ||
        message.chatFinishReason === ChatFinishReason.Stop;

    if (normalMessage) {
        return <></>;
    }

    if (
        message.chatFinishReason === ChatFinishReason.ContentFilter &&
        onEditClick
    ) {
        return (
            <Paragraph cssClassName={`${BASE_CLASS}__markdown-paragraph`}>
                <LinkButton onClick={onEditClick}>Edit</LinkButton>
                <span> the previous message or start a new conversation.</span>
            </Paragraph>
        );
    }

    if (message.chatFinishReason === ChatFinishReason.Length && onEditClick) {
        return (
            <Paragraph cssClassName={`${BASE_CLASS}__markdown-paragraph`}>
                <span>You can either </span>
                <LinkButton onClick={onEditClick}>edit</LinkButton>
                <span>
                    {" "}
                    the previous message to be more concise or start a new
                    conversation.
                </span>
            </Paragraph>
        );
    }

    if (!onRetryClick) {
        return <></>;
    }

    return (
        <Paragraph cssClassName={`${BASE_CLASS}__markdown-paragraph`}>
            <span>Would you like to</span>{" "}
            <LinkButton onClick={onRetryClick}>retry</LinkButton>?
        </Paragraph>
    );
}

interface LinkButtonProps {
    children: React.ReactNode;
    onClick: () => void;
}

const LinkButton: React.FC<LinkButtonProps> = ({ onClick, children }) => (
    <Button
        type={ButtonTypes.Button}
        style={ButtonStyles.Anchor}
        onClick={onClick}>
        {children}
    </Button>
);

const MemoizedMarkdown = React.memo(
    ({
        content,
        citationMap,
        nextNumberRef,
        handleCitationClick,
    }: MemoizedMarkdownProps) => {
        const renderCitation = useCallback(
            ({ node }: CitationProps) => (
                <InlineCitationButton
                    node={node}
                    onClick={handleCitationClick}
                />
            ),
            [handleCitationClick]
        );

        const enhancedComponents = useMemo(
            () =>
                ({
                    ...customComponents,
                    citation: renderCitation,
                    span: ({ node, className, children, ...props }: any) => {
                        if (className === "katex") {
                            const latexContent =
                                node?.data?.value || node?.value;
                            return (
                                <MathWithPopup originalContent={latexContent}>
                                    <span className={className} {...props}>
                                        {children}
                                    </span>
                                </MathWithPopup>
                            );
                        }
                        return (
                            <span className={className} {...props}>
                                {children}
                            </span>
                        );
                    },
                    div: ({ node, className, children, ...props }: any) => {
                        if (className?.includes("katex-display")) {
                            const latexContent =
                                node?.data?.value || node?.value;
                            return (
                                <MathWithPopup originalContent={latexContent}>
                                    <div className={className} {...props}>
                                        {children}
                                    </div>
                                </MathWithPopup>
                            );
                        }
                        return (
                            <div className={className} {...props}>
                                {children}
                            </div>
                        );
                    },
                }) as any,
            [renderCitation]
        );

        const wrappedContent = wrapLatexLines(content);

        return (
            <ErrorBoundary
                fallbackRender={() => (
                    <div className={`${BASE_CLASS}__markdown-fallback`}>
                        {content}
                    </div>
                )}
                resetKeys={[wrappedContent]}>
                <ReactMarkdown
                    remarkPlugins={[
                        remarkMath,
                        remarkGfm,
                        [replaceCitations, { citationMap, nextNumberRef }],
                    ]}
                    rehypePlugins={[rehypeKatex]}
                    components={enhancedComponents}>
                    {wrappedContent}
                </ReactMarkdown>
            </ErrorBoundary>
        );
    },
    compareMarkdownProps
);
